import GPXParser from 'gpxparser';
import { getSupabaseClient } from '@/lib/supabase';

// GPX 파싱 관련 타입들
interface GPXWaypoint {
  lat: number;
  lon: number;
  ele?: number;
  name?: string;
  desc?: string;
}

interface GPXPoint {
  lat: number;
  lon: number;
  ele?: number;
}

interface GPXTrack {
  points: GPXPoint[];
}

interface ParsedGPX {
  tracks: GPXTrack[];
  waypoints: GPXWaypoint[];
}

// 메모이제이션을 위한 캐시 (영구 캐시로 변경)
const gpxCache = new Map<string, TrailData>();
const CACHE_TTL = Infinity; // 영구 캐시 (불변 데이터이므로)
const cacheTimestamps = new Map<string, number>();

export interface TrailPoint {
  lat: number;
  lon: number;
  ele: number;
  distance: number; // 누적 거리 (km)
}

export interface POIPoint {
  lat: number;
  lon: number;
  name: string;
  type: 'start' | 'end' | 'viewpoint' | 'rest' | 'landmark' | 'water' | 'food' | 'danger' | 'waypoint' | 'accommodation';
  description?: string;
  elevation?: number;
  accommodation_id?: string; // For accommodation type POIs
}

export interface TrailStats {
  totalDistance: number; // 총 거리 (km)
  elevationGain: number; // 누적 상승 고도 (m)
  elevationLoss: number; // 누적 하강 고도 (m)
  maxElevation: number; // 최고 고도 (m)
  minElevation: number; // 최저 고도 (m)
  estimatedTime: number; // 예상 소요시간 (시간)
  difficulty: 'Easy' | 'Moderate' | 'Hard'; // 난이도
  bounds: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };
}

export interface TrailData {
  name: string;
  points: TrailPoint[];
  stats: TrailStats;
  geoJSON: GeoJSON.FeatureCollection;
  pois: POIPoint[];
  elevationProfiles?: {
    high: Array<{distance: number, elevation: number}>;
    medium: Array<{distance: number, elevation: number}>;
    low: Array<{distance: number, elevation: number}>;
  };
}

// 두 지점 간의 거리 계산 (Haversine formula)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // 지구 반지름 (km)
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Douglas-Peucker 알고리즘으로 포인트 단순화
function simplifyTrail(points: TrailPoint[], tolerance: number = 0.0001): TrailPoint[] {
  if (points.length <= 2) return points;
  
  // 가장 먼 점 찾기
  let maxDistance = 0;
  let maxIndex = 0;
  const start = points[0];
  const end = points[points.length - 1];
  
  for (let i = 1; i < points.length - 1; i++) {
    const point = points[i];
    const distance = perpendicularDistance(point, start, end);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }
  
  // 재귀적으로 단순화
  if (maxDistance > tolerance) {
    const left = simplifyTrail(points.slice(0, maxIndex + 1), tolerance);
    const right = simplifyTrail(points.slice(maxIndex), tolerance);
    return left.slice(0, -1).concat(right);
  } else {
    return [start, end];
  }
}

// 점과 선분 사이의 수직 거리 계산
function perpendicularDistance(point: TrailPoint, lineStart: TrailPoint, lineEnd: TrailPoint): number {
  const A = lineEnd.lat - lineStart.lat;
  const B = lineStart.lon - lineEnd.lon;
  const C = lineEnd.lon * lineStart.lat - lineStart.lon * lineEnd.lat;
  
  return Math.abs(A * point.lon + B * point.lat + C) / Math.sqrt(A * A + B * B);
}

// 난이도 계산
function calculateDifficulty(stats: TrailStats): 'Easy' | 'Moderate' | 'Hard' {
  // 거리, 고도차, 예상시간을 종합적으로 고려
  const distanceScore = Math.min(stats.totalDistance / 20, 1); // 20km 이상이면 1점
  const elevationScore = Math.min(stats.elevationGain / 1000, 1); // 1000m 이상이면 1점
  const timeScore = Math.min(stats.estimatedTime / 8, 1); // 8시간 이상이면 1점
  
  const totalScore = (distanceScore + elevationScore + timeScore) / 3;
  
  if (totalScore < 0.3) return 'Easy';
  if (totalScore < 0.7) return 'Moderate';
  return 'Hard';
}

// 예상 소요시간 계산 (Naismith's rule)
function calculateEstimatedTime(distance: number, elevationGain: number): number {
  // 기본: 4km/h + 고도 600m당 1시간 추가
  const baseTime = distance / 4; // 시간
  const elevationTime = elevationGain / 600; // 시간
  return baseTime + elevationTime;
}

// 트레일 통계 계산
function calculateTrailStats(points: TrailPoint[], courseData?: {
  estimated_time?: number | null;
  difficulty?: string | null;
}): TrailStats {
  if (points.length === 0) {
    throw new Error('No points to calculate stats');
  }

  let totalDistance = 0;
  let elevationGain = 0;
  let elevationLoss = 0;
  let maxElevation = points[0].ele;
  let minElevation = points[0].ele;
  let minLat = points[0].lat;
  let maxLat = points[0].lat;
  let minLon = points[0].lon;
  let maxLon = points[0].lon;

  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    
    // 경계 계산
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLon = Math.min(minLon, point.lon);
    maxLon = Math.max(maxLon, point.lon);
    
    // 고도 통계
    maxElevation = Math.max(maxElevation, point.ele);
    minElevation = Math.min(minElevation, point.ele);

    if (i > 0) {
      const prevPoint = points[i - 1];
      
      // 거리 계산
      const segmentDistance = calculateDistance(
        prevPoint.lat, prevPoint.lon, 
        point.lat, point.lon
      );
      totalDistance += segmentDistance;
      point.distance = totalDistance;
      
      // 고도차 계산
      const elevationChange = point.ele - prevPoint.ele;
      if (elevationChange > 0) {
        elevationGain += elevationChange;
      } else {
        elevationLoss += Math.abs(elevationChange);
      }
    } else {
      point.distance = 0;
    }
  }

  // 예상 시간: courses 테이블 값을 우선 사용, 없으면 계산값 사용
  const calculatedTime = calculateEstimatedTime(totalDistance, elevationGain);
  const estimatedTime = courseData?.estimated_time 
    ? courseData.estimated_time / 60 // 분을 시간으로 변환
    : calculatedTime;
  
  const stats: TrailStats = {
    totalDistance,
    elevationGain,
    elevationLoss,
    maxElevation,
    minElevation,
    estimatedTime,
    difficulty: 'Easy', // 임시값, 나중에 계산
    bounds: {
      minLat,
      maxLat,
      minLon,
      maxLon
    }
  };

  // 난이도: courses 테이블 값을 우선 사용, 없으면 계산값 사용
  if (courseData?.difficulty) {
    switch (courseData.difficulty) {
      case '쉬움':
        stats.difficulty = 'Easy';
        break;
      case '보통':
        stats.difficulty = 'Moderate';
        break;
      case '어려움':
        stats.difficulty = 'Hard';
        break;
      default:
        stats.difficulty = calculateDifficulty(stats);
    }
  } else {
    stats.difficulty = calculateDifficulty(stats);
  }
  
  return stats;
}

// GPX에서 POI 정보 추출
function extractPOIs(gpx: ParsedGPX, trailPoints: TrailPoint[]): POIPoint[] {
  const pois: POIPoint[] = [];
  
  // 시작점과 끝점은 항상 추가
  if (trailPoints.length > 0) {
    const startPoint = trailPoints[0];
    const endPoint = trailPoints[trailPoints.length - 1];
    
    pois.push({
      lat: startPoint.lat,
      lon: startPoint.lon,
      name: '출발점',
      type: 'start',
      elevation: startPoint.ele,
      description: '트레킹 출발 지점입니다.'
    });
    
    pois.push({
      lat: endPoint.lat,
      lon: endPoint.lon,
      name: '도착점',
      type: 'end',
      elevation: endPoint.ele,
      description: '트레킹 도착 지점입니다.'
    });
  }
  
  // GPX waypoints 추출
  if (gpx.waypoints && gpx.waypoints.length > 0) {
    gpx.waypoints.forEach((waypoint: GPXWaypoint) => {
      let type: POIPoint['type'] = 'waypoint';
      let name = waypoint.name || '지점';
      
      // 이름이나 설명으로 POI 타입 추정
      const nameDesc = (waypoint.name || '').toLowerCase() + ' ' + (waypoint.desc || '').toLowerCase();
      
      if (nameDesc.includes('전망') || nameDesc.includes('viewpoint') || nameDesc.includes('view')) {
        type = 'viewpoint';
        name = waypoint.name || '전망대';
      } else if (nameDesc.includes('휴게') || nameDesc.includes('rest') || nameDesc.includes('쉼터')) {
        type = 'rest';
        name = waypoint.name || '휴게소';
      } else if (nameDesc.includes('물') || nameDesc.includes('water') || nameDesc.includes('샘')) {
        type = 'water';
        name = waypoint.name || '물';
      } else if (nameDesc.includes('식당') || nameDesc.includes('food') || nameDesc.includes('restaurant')) {
        type = 'food';
        name = waypoint.name || '음식점';
      } else if (nameDesc.includes('위험') || nameDesc.includes('danger') || nameDesc.includes('주의')) {
        type = 'danger';
        name = waypoint.name || '주의구간';
      } else if (nameDesc.includes('랜드마크') || nameDesc.includes('landmark') || nameDesc.includes('명소')) {
        type = 'landmark';
        name = waypoint.name || '랜드마크';
      }
      
      pois.push({
        lat: waypoint.lat,
        lon: waypoint.lon,
        name,
        type,
        elevation: waypoint.ele,
        description: waypoint.desc || waypoint.cmt
      });
    });
  }
  
  // 고도 변화가 큰 지점들을 전망대로 추정 (간단한 알고리즘)
  if (trailPoints.length > 10) {
    const elevationThreshold = 50; // 50m 이상 고도 변화
    let lastPeakIndex = -1;
    
    for (let i = 5; i < trailPoints.length - 5; i++) {
      const current = trailPoints[i];
      const before = trailPoints[i - 5];
      const after = trailPoints[i + 5];
      
      // 주변보다 높은 지점이고, 이전 피크와 충분히 떨어져 있으면
      if (current.ele > before.ele + elevationThreshold && 
          current.ele > after.ele + elevationThreshold &&
          (lastPeakIndex === -1 || i - lastPeakIndex > 20)) {
        
        // 이미 근처에 POI가 있는지 확인
        const nearbyPOI = pois.some(poi => 
          calculateDistance(poi.lat, poi.lon, current.lat, current.lon) < 0.1 // 100m 이내
        );
        
        if (!nearbyPOI) {
          pois.push({
            lat: current.lat,
            lon: current.lon,
            name: '고지대',
            type: 'viewpoint',
            elevation: current.ele,
            description: `고도 ${Math.round(current.ele)}m 전망 지점`
          });
          lastPeakIndex = i;
        }
      }
    }
  }
  
  return pois;
}

// GPX를 GeoJSON으로 변환
function createGeoJSON(points: TrailPoint[], name: string): GeoJSON.FeatureCollection {
  const coordinates = points.map(point => [point.lon, point.lat, point.ele]);
  
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {
          name,
          type: 'trail'
        },
        geometry: {
          type: 'LineString',
          coordinates
        }
      }
    ]
  };
}

// 캐시 정리 함수
function clearExpiredCache(): void {
  const now = Date.now();
  for (const [key, timestamp] of cacheTimestamps.entries()) {
    if (now - timestamp > CACHE_TTL) {
      gpxCache.delete(key);
      cacheTimestamps.delete(key);
    }
  }
}

// 캐시 키 생성
function createCacheKey(courseId: string, courseData?: {
  estimated_time?: number | null;
  name?: string;
  difficulty?: string | null;
}): string {
  const dataKey = courseData ? JSON.stringify({
    estimated_time: courseData.estimated_time,
    difficulty: courseData.difficulty
  }) : '';
  return `${courseId}-${dataKey}`;
}

// GPX 데이터 로드 (DB 우선, fallback으로 GPX 파일)
export async function loadGPXData(courseId: string, courseData?: {
  estimated_time?: number | null;
  name?: string;
  difficulty?: string | null;
}): Promise<TrailData> {
  // 캐시 정리 (영구 캐시이므로 실제로는 정리하지 않음)
  clearExpiredCache();
  
  // 캐시 확인
  const cacheKey = createCacheKey(courseId, courseData);
  const cached = gpxCache.get(cacheKey);
  if (cached) {
    console.log(`📦 Memory cache hit for course ${courseId}`);
    return cached;
  }
  
  try {
    console.log(`🔄 Loading trail data for course ${courseId}`);
    
    // 1. 먼저 DB에서 전처리된 데이터 시도
    const dbResult = await loadFromDatabase(courseId, courseData);
    if (dbResult) {
      console.log(`💾 DB data loaded for course ${courseId}`);
      
      // 캐시에 저장
      gpxCache.set(cacheKey, dbResult);
      cacheTimestamps.set(cacheKey, Date.now());
      
      return dbResult;
    }
    
    // 2. DB에 데이터가 없으면 GPX 파일에서 로드 (fallback)
    console.log(`📄 Falling back to GPX file for course ${courseId}`);
    const gpxResult = await loadFromGPXFile(courseId, courseData);
    
    // 캐시에 저장
    gpxCache.set(cacheKey, gpxResult);
    cacheTimestamps.set(cacheKey, Date.now());
    console.log(`💾 Cached GPX data for course ${courseId}`);
    
    return gpxResult;
    
  } catch (error) {
    console.error('Error loading trail data:', error);
    throw error;
  }
}

// DB에서 전처리된 데이터 로드
async function loadFromDatabase(courseId: string, courseData?: {
  estimated_time?: number | null;
  name?: string;
  difficulty?: string | null;
}): Promise<TrailData | null> {
  const supabase = getSupabaseClient();
  try {
    const { data, error } = await supabase
      .from('courses')
      .select('trail_data, name, estimated_time, difficulty')
      .eq('course_id', courseId)
      .single();

    if (error || !data || !data.trail_data) {
      return null;
    }

    const trailData = data.trail_data;
    
    // 전처리된 데이터 검증
    if (!trailData.optimizedPoints || !trailData.preCalculatedStats || 
        !trailData.geoJSON || !trailData.pois) {
      console.warn(`⚠️ Invalid trail_data structure for course ${courseId}`);
      return null;
    }

    // TrailData 형식으로 변환
    const result: TrailData = {
      name: courseData?.name || data.name || trailData.metadata?.originalName || `Course ${courseId}`,
      points: trailData.optimizedPoints,
      stats: trailData.preCalculatedStats,
      geoJSON: trailData.geoJSON,
      pois: trailData.pois
    };

    // 고도 프로필이 전처리되어 있으면 추가
    if (trailData.elevationProfiles) {
      result.elevationProfiles = trailData.elevationProfiles;
      console.log(`📈 Using precomputed elevation profiles for course ${courseId}`);
    }

    return result;

  } catch (error) {
    console.warn(`⚠️ DB load failed for course ${courseId}:`, error);
    return null;
  }
}

// GPX 파일에서 로드 (기존 로직)
async function loadFromGPXFile(courseId: string, courseData?: {
  estimated_time?: number | null;
  name?: string;
  difficulty?: string | null;
}): Promise<TrailData> {
  const response = await fetch(`/gpx/${courseId}.gpx`);
  
  if (!response.ok) {
    throw new Error(`Failed to load GPX file for course ${courseId}`);
  }
  
  const gpxText = await response.text();
  
  // GPX 파싱
  const gpx = new GPXParser();
  gpx.parse(gpxText);
  
  if (!gpx.tracks || gpx.tracks.length === 0) {
    throw new Error('No tracks found in GPX file');
  }
  
  const track = gpx.tracks[0];
  const rawPoints: TrailPoint[] = track.points.map((point: GPXPoint) => ({
    lat: point.lat,
    lon: point.lon,
    ele: point.ele || 0,
    distance: 0
  }));

  if (rawPoints.length === 0) {
    throw new Error('No points found in GPX track');
  }

  // 포인트 단순화 (성능 최적화)
  let simplifiedPoints: TrailPoint[];
  if (rawPoints.length > 2000) {
    simplifiedPoints = simplifyTrail(rawPoints, 0.0002);
  } else if (rawPoints.length > 1000) {
    simplifiedPoints = simplifyTrail(rawPoints, 0.0001);
  } else {
    simplifiedPoints = rawPoints;
  }
  
  console.log(`📊 Points optimized: ${rawPoints.length} → ${simplifiedPoints.length} (${Math.round((1 - simplifiedPoints.length / rawPoints.length) * 100)}% reduction)`);

  // 통계 계산
  const stats = calculateTrailStats(simplifiedPoints, courseData);
  
  // POI 추출
  const pois = extractPOIs(gpx, simplifiedPoints);
  
  // GeoJSON 생성
  const geoJSON = createGeoJSON(simplifiedPoints, track.name || 'Trail');

  return {
    name: courseData?.name || track.name || `Course ${courseId}`,
    points: simplifiedPoints,
    stats,
    geoJSON,
    pois
  };
}

// 지도 줌 레벨 계산
export function calculateZoomLevel(bounds: TrailStats['bounds']): number {
  const latDiff = bounds.maxLat - bounds.minLat;
  const lonDiff = bounds.maxLon - bounds.minLon;
  const maxDiff = Math.max(latDiff, lonDiff);
  
  if (maxDiff > 1) return 8;
  if (maxDiff > 0.5) return 9;
  if (maxDiff > 0.1) return 11;
  if (maxDiff > 0.05) return 12;
  if (maxDiff > 0.01) return 14;
  return 15;
}

// 메모이제이션된 고도 프로필 생성
const elevationCache = new Map<string, Array<{distance: number, elevation: number}>>();

export function createElevationProfile(points: TrailPoint[], maxPoints: number = 200): Array<{distance: number, elevation: number}> {
  // 캐시 키 생성
  const cacheKey = `${points.length}-${maxPoints}-${points[0]?.distance}-${points[points.length-1]?.distance}`;
  
  // 캐시 확인
  const cached = elevationCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  let result: Array<{distance: number, elevation: number}>;
  
  if (points.length <= maxPoints) {
    result = points.map(point => ({
      distance: point.distance,
      elevation: point.ele
    }));
  } else {
    // 균등 간격으로 샘플링
    const step = points.length / maxPoints;
    const sampledPoints = [];
    
    for (let i = 0; i < maxPoints; i++) {
      const index = Math.floor(i * step);
      if (index < points.length) {
        sampledPoints.push({
          distance: points[index].distance,
          elevation: points[index].ele
        });
      }
    }
    
    result = sampledPoints;
  }
  
  // 캐시에 저장 (최대 50개 항목만 유지)
  if (elevationCache.size >= 50) {
    const firstKey = elevationCache.keys().next().value;
    elevationCache.delete(firstKey);
  }
  elevationCache.set(cacheKey, result);
  
  return result;
}

// 캐시 수동 정리 함수
export function clearGPXCache(): void {
  gpxCache.clear();
  cacheTimestamps.clear();
  elevationCache.clear();
  console.log('🧹 GPX cache cleared');
}